import React from 'react';
import { AlertOctagon, Braces, X } from 'lucide-react';

export interface VistaErrorDetailProps {
  /** The reason title — what went wrong, in the operator's words. */
  title: string;
  /** Who and what it hit: "LOPEZ, MANUEL J · #2536 · order 5775941". */
  meta: string;
  /** Plain-language explanation. Always the first thing read. */
  plain: string;
  /** The line Vista itself returned, when one could be extracted. Often null. */
  vistaSaid: string | null;
  /** Raw payload — always available, always one disclosure down. */
  raw: unknown;
  onClose: () => void;
}

/**
 * The error detail, stacked above the export modal.
 *
 * Replaces the v1 modal's JSON dump. The ordering is the point: plain language first,
 * then what Vista literally said, then what to do, and only then the payload. The
 * payload is never removed — when Vista returns something we have not seen before, it
 * is the only thing that will explain it.
 */
const VistaErrorDetail: React.FC<VistaErrorDetailProps> = ({ title, meta, plain, vistaSaid, raw, onClose }) => (
  <div className="vx-errbg" onClick={onClose}>
    {/* `trb-modal` is load-bearing, not decoration: the board scopes all of its modal
        chrome as `.trb .trb-modal .modal-head/.modal-body/.modal-foot`, so a panel
        without that class gets none of it. `.vx-err` only overrides the width. */}
    <div className="trb-modal vx-err" onClick={(e) => e.stopPropagation()}>
      <div className="modal-head">
        <span className="mi bad"><AlertOctagon /></span>
        <div>
          <h3>{title}</h3>
          <div className="ms">{meta}</div>
        </div>
        <button className="x" onClick={onClose} type="button" aria-label="Close"><X /></button>
      </div>

      <div className="modal-body">
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55 }}>{plain}</p>

        {/* No "To fix" anywhere in the error path — the advice we could derive from the
            payload was generic enough to be noise on top of what Vista actually said. */}
        {vistaSaid && (
          <div className="vx-sumcard">
            <div className="vx-sumrow">
              <span className="lb">Vista said</span>
              <span className="vl mono" style={{ maxWidth: '62%', wordBreak: 'break-word' }}>{vistaSaid}</span>
            </div>
          </div>
        )}

        <details className="vx-details">
          <summary><Braces /> Technical details — the raw Vista response</summary>
          <pre>{safeStringify(raw)}</pre>
        </details>
      </div>

      <div className="modal-foot">
        <button className="tr-btn" onClick={onClose} type="button">Close</button>
      </div>
    </div>
  </div>
);

/** errorDetails is `any` on the API side and can be a string, an object, or circular. */
const safeStringify = (value: unknown): string => {
  if (value == null) return 'No payload was returned with this error.';
  if (typeof value === 'string') return value;
  const seen = new WeakSet();
  try {
    return JSON.stringify(value, (_k, v) => {
      if (typeof v === 'object' && v !== null) {
        if (seen.has(v)) return '[circular]';
        seen.add(v);
      }
      return v;
    }, 2) ?? String(value);
  } catch {
    return String(value);
  }
};

export default VistaErrorDetail;
